import { useState } from "react";
import { Link } from "react-router-dom";
import { useClerk } from "@clerk/react";
import { toast } from "sonner";
import { ChevronRight, Crown, Library, LogOut, MessageSquare, Moon, Smartphone, Users } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useApi, errMsg } from "@/lib/api";
import { useThemeToggle } from "@/lib/theme";
import { Card, PageTitle } from "@/components/common";
import { Field } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const LinkRow = ({ to, icon: Icon, label, desc, testId }) => (
  <Link to={to} className="flex items-center gap-3 rounded-xl p-3 transition-colors hover:bg-secondary/70 active:scale-[0.99]" data-testid={testId}>
    <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
    <span className="min-w-0 flex-1"><span className="block font-semibold">{label}</span><span className="block text-xs text-muted-foreground">{desc}</span></span>
    <ChevronRight className="h-5 w-5 text-muted-foreground" />
  </Link>
);

function Profile() {
  const api = useApi();
  const { me, isAdmin, reload } = useMe();
  const [name, setName] = useState(me.name);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api("patch", "/me", { name }); await reload(); toast.success("Profilo aggiornato"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="mb-4 flex items-center gap-3">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-primary font-heading text-lg font-bold text-primary-foreground">{me.name?.[0]?.toUpperCase()}</span>
        <div className="min-w-0">
          <p className="truncate font-heading text-lg font-bold" data-testid="profile-name">{me.name}</p>
          <p className="truncate text-sm text-muted-foreground" data-testid="profile-email">{me.email}</p>
        </div>
        <span className={`ml-auto inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${isAdmin ? "bg-amber-400/15 text-amber-700 dark:text-amber-300" : "bg-secondary"}`} data-testid="profile-role">
          {isAdmin && <Crown className="h-3 w-3" />}{isAdmin ? "Rappresentante" : "Alunno"}
        </span>
      </div>
      <form onSubmit={save} className="flex items-end gap-2">
        <div className="flex-1"><Field label="Nome visualizzato"><Input className="h-11" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" /></Field></div>
        <Button type="submit" disabled={busy || !name.trim() || name === me.name} className="h-11 rounded-full" data-testid="profile-save-btn">Salva</Button>
      </form>
    </Card>
  );
}

export default function Altro() {
  const { isAdmin, me } = useMe();
  const { signOut } = useClerk();
  const { dark, toggle } = useThemeToggle();
  return (
    <div className="space-y-4 lg:max-w-2xl">
      <PageTitle title="Impostazioni" subtitle={me.class_info?.name} />
      <Profile />
      <Card className="space-y-1 p-2">
        <LinkRow to="/portali" icon={Library} label="Libri digitali" desc="Portali e credenziali della classe" testId="altro-link-portali" />
        <LinkRow to="/messaggi" icon={MessageSquare} label="Messaggi ai rappresentanti" desc="Segnalazioni e comunicazioni, anche anonime" testId="altro-link-messaggi" />
        {isAdmin && <LinkRow to="/classe" icon={Users} label="Gestione classe" desc="Alunni, ruoli e dati della classe" testId="altro-link-classe" />}
      </Card>
      <Card className="space-y-4">
        <label className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-3"><Moon className="h-5 w-5 text-primary" /><span className="font-semibold">Tema scuro</span></span>
          <Switch checked={dark} onCheckedChange={toggle} data-testid="theme-toggle-btn" />
        </label>
        <div className="flex items-start gap-3 border-t border-border pt-4">
          <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Aggiungila alla Home.</span> iPhone: Safari → Condividi → Aggiungi alla schermata Home. Android: menu di Chrome → Installa app.</p>
        </div>
      </Card>
      <Button variant="outline" className="h-12 w-full rounded-full text-base text-destructive" onClick={() => signOut()} data-testid="settings-signout-btn">
        <LogOut className="mr-2 h-4 w-4" />Esci
      </Button>
    </div>
  );
}
